// src/receptionist/screens/Settings.tsx
//
// The receptionist's own settings page (ourteam-frontend#3614). The rail's
// Settings item used to point at the shared /setting page, which picks its
// layout from the Oura and Training roles and fell through to the recruiter
// sidebar for anyone else — so a merchant clicking Settings appeared to have
// left the product.
//
// Extending that page the way Oura did would have meant editing
// src/pages/setting/index.tsx AND src/containers/setting-v2.tsx, whose tab
// logic already carries recruiter, trainer and operator conditionals. A
// fourth product in those expressions is a mistake waiting to break a live
// product. So this is a new page inside the receptionist shell, composed
// from the existing sub-containers — imported unchanged, which is not the
// same as modifying them.

import { useState } from "react";
import { UserIcon, UsersIcon } from "lucide-react";
import { PageHeader } from "../components/kit";
import { SURFACE } from "@oura/ui";
import { cn } from "@oura/lib/cn";
import SettingProfileFormV2 from "@container/setting-profile-form-v2";
import SettingUserPermissions from "@container/setting-user-permission";
import { PageSectionTabs } from "../components/ui";
import type { ReceptionistRole } from "../role";

type Section = "profile" | "people";

export function SettingsScreen({ role }: { role: ReceptionistRole }) {
    // People and access is a merchant owner's concern — they manage their own
    // team. ourteam staff manage nobody from here.
    const canManagePeople = role === "receptionist-client";
    const [section, setSection] = useState<Section>("profile");

    return (
        <div className="w-full">
            <PageHeader
                title="Settings"
                subtitle={canManagePeople ? "Your own details, and who on your team can open this console." : "Your own details."}
            />
            {canManagePeople && (
                <PageSectionTabs<Section>
                    value={section}
                    onChange={setSection}
                    options={[
                        { value: "profile", label: "Your details", icon: UserIcon },
                        { value: "people", label: "Your team", icon: UsersIcon },
                    ]}
                />
            )}
            {/* The tab names the section, as on Oura: the form sits in an Oura card, no second heading. */}
            <div className={cn(SURFACE.card, "p-6")}>
                {section === "people" && canManagePeople ? <SettingUserPermissions /> : <SettingProfileFormV2 />}
            </div>
        </div>
    );
}
